import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/colors";
import { PageHeader, Pill } from "@/components/common";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { UserFormDialog } from "@/components/settings/UserFormDialog";
import { useDepartments } from "@/hooks/useDepartments";
import { Button } from "@/components/ui/button";

const roleCls = (u) => (u.role === "admin" ? "bg-slate-800 text-white border-slate-800" : u.can_supervise ? "bg-[#EAF2F2] text-[#24474A] border-[#24474A]/30" : "bg-white text-slate-700 border-slate-300");

const UserRow = ({ u, me, isSuper, names, onEdit, onDelete }) => {
  const locked = u.role === "admin" && !isSuper;
  return (
    <tr className="border-t border-slate-100" data-testid={`admin-user-row-${u.id}`}>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-[#EAF2F2] text-[#24474A] grid place-items-center text-xs font-bold shrink-0">{initials(u.name)}</span>
          <div className="leading-tight min-w-0">
            <div className="font-bold text-sm text-slate-800">{u.name}</div>
            <div className="text-xs text-slate-500 truncate">{u.email}</div>
          </div>
        </div>
      </td>
      <td className="px-2"><Pill cls={roleCls(u)} testid={`admin-user-role-${u.id}`}>{u.role_name}</Pill></td>
      <td className="px-2 text-sm text-slate-600">{u.department || "—"}</td>
      <td className="px-2 text-sm text-slate-600">{names[u.supervisor_id] || "—"}</td>
      <td className="px-3 text-right whitespace-nowrap">
        <Button size="icon" variant="ghost" disabled={locked} onClick={() => onEdit(u)} data-testid={`admin-edit-user-${u.id}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" disabled={u.id === me || locked} onClick={() => onDelete(u)} data-testid={`admin-delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </td>
    </tr>
  );
};

export default function UsersSettings() {
  const { user } = useAuth();
  const isSuper = user.role === "admin";
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const { departments } = useDepartments();
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    const [u, r] = await Promise.all([api.get("/admin/users"), api.get("/roles")]);
    setUsers(u.data);
    setRoles(r.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will lose access immediately.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const names = Object.fromEntries(users.map((u) => [u.id, u.name]));
  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage people, their role, department and supervisor.">
        <Button className="rounded-full" onClick={() => setDialog({ user: null })} data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Add user</Button>
      </PageHeader>
      <SettingsTabs />
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="admin-users-table">
        <table className="w-full min-w-[820px] border-collapse">
          <thead>
            <tr className="bg-slate-50/70 text-left text-[11px] uppercase tracking-[0.15em] text-slate-500">
              <th className="px-4 py-3">Person</th><th className="px-2 py-3">Role</th><th className="px-2 py-3">Department</th><th className="px-2 py-3">Supervisor</th><th className="px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => <UserRow key={u.id} u={u} me={user.id} isSuper={isSuper} names={names} onEdit={(x) => setDialog({ user: x })} onDelete={remove} />)}
          </tbody>
        </table>
      </div>
      <UserFormDialog
        open={!!dialog} user={dialog?.user} roles={isSuper ? roles : roles.filter((r) => (user.manages || []).includes(r.key) || r.key === dialog?.user?.role)} departments={departments} isSuper={isSuper}
        supervisors={users.filter((u) => u.can_supervise)} onClose={() => setDialog(null)} onSaved={load}
      />
    </div>
  );
}
